import { useGiftCards } from '../../store/giftCards';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { CheckIcon } from '../../components/icons';

/**
 * The card numbers and PINs of a just-sold gift card.
 *
 * This is the one and only moment either value is visible. PINs are stored as
 * salted hashes and card numbers are masked everywhere else, so a cashier who
 * dismisses this without writing them down has to void and re-ring the sale.
 * The dialog therefore says so, and has no way to close by clicking away.
 *
 * It renders at app level rather than inside the payment modal because that
 * modal closes and resets the cart the instant the charge succeeds — which is
 * exactly when these values arrive.
 */
export function IssuedGiftCardsDialog(): JSX.Element | null {
  const { t: tr } = useLang();
  const issued = useGiftCards((s) => s.issued);
  const clearIssued = useGiftCards((s) => s.clearIssued);

  if (issued.length === 0) return null;

  return (
    <div className="modal-overlay show">
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{ background: '#f3e8ff', color: '#7e22ce' }}
          >
            🎁
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {issued.length === 1
                ? tr({ en: 'Gift card ready', ar: 'البطاقة جاهزة' })
                : tr({ en: `${issued.length} gift cards ready`, ar: `${issued.length} بطاقات جاهزة` })}
            </div>
            <div className="modal-header-name-ar">
              {tr({
                en: 'Write these down or hand them over now.',
                ar: 'دوّن هذه البيانات أو سلّمها للعميل الآن.',
              })}
            </div>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          <div className="pay-refund-banner" style={{ marginBottom: 12 }}>
            {tr({
              en: 'This is the only time the PIN can be read — it is stored encrypted.',
              ar: 'هذه المرة الوحيدة لقراءة الرمز السري — فهو مخزّن مشفّرًا.',
            })}
          </div>

          {issued.map((card) => (
            <div key={card.card_number} className="pay-giftcard-issued">
              <div className="pay-giftcard-issued-number ops-mono">{card.card_number}</div>
              <div className="pay-giftcard-issued-meta">
                <span>
                  {tr({ en: 'Value', ar: 'القيمة' })}: <strong>{fmt(card.amount)}</strong>
                </span>
                {card.pin && (
                  <span>
                    {tr({ en: 'PIN', ar: 'الرمز' })}:{' '}
                    <strong className="ops-mono">{card.pin}</strong>
                  </span>
                )}
                {card.expires_at && (
                  <span>
                    {tr({ en: 'Expires', ar: 'تنتهي' })}{' '}
                    {new Date(card.expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer pay-footer">
          <button className="btn btn-primary pay-confirm-btn" onClick={clearIssued} autoFocus>
            <CheckIcon size={16} />
            {tr({ en: 'Done — handed over', ar: 'تم — سُلّمت' })}
          </button>
        </div>
      </div>
    </div>
  );
}
